import type { BinStatus, Owner, Site, SiteType, SiteWithLocations, StorageBin } from '../features/locations/types';
import { OWNERS } from '../features/locations/types';

const API_URL = process.env.REACT_APP_API_URL || '/api';

interface ApiBin {
  id: string;
  name: string;
  material: string;
  materialId?: string | null;
  unitOfMeasure?: string | null;
  owner?: string | null;
  quantity?: number;
  minQuantity?: number | null;
  maxQuantity?: number | null;
  status?: BinStatus;
}

interface ApiLocation {
  id: string;
  name: string;
  type?: string;
  bins: ApiBin[];
}

interface ApiSite {
  id: string;
  type: string;
  name: string;
  number: string | null;
  owner: string;
  latitude: number | null;
  longitude: number | null;
  description: string | null;
  note: string | null;
  serviceAreaId?: string;
  serviceAreaName?: string;
  foremanAreaIds?: string[];
  attention?: { negative: number; belowMin: number; overMax: number };
  lastCountAt?: string | null;
  locations: ApiLocation[];
}

interface SitesResponse {
  sites: ApiSite[];
}

const SITE_TYPES: SiteType[] = ['pit', 'yard', 'rockfall'];

function asSiteType(value: string): SiteType {
  return (SITE_TYPES as string[]).includes(value) ? (value as SiteType) : 'pit';
}

function asOwner(value: string): Owner {
  return (OWNERS as string[]).includes(value) ? (value as Owner) : 'Unknown';
}

function mapSite(row: ApiSite): Site {
  return {
    id: row.id,
    type: asSiteType(row.type),
    name: row.name,
    number: row.number,
    owner: asOwner(row.owner),
    latitude: row.latitude,
    longitude: row.longitude,
    description: row.description,
    note: row.note,
    serviceAreaId: row.serviceAreaId ?? '',
    serviceAreaName: row.serviceAreaName ?? '',
    foremanAreaIds: row.foremanAreaIds ?? [],
    attention: row.attention ?? { negative: 0, belowMin: 0, overMax: 0 },
    lastCountAt: row.lastCountAt ?? null,
    locations: (row.locations ?? []).map((location, locationIndex) => ({
      id: location.id ?? `loc-${locationIndex}`,
      name: location.name,
      type: location.type ?? '',
      bins: (location.bins ?? []).map((bin, binIndex) => ({
        id: bin.id ?? `bin-${locationIndex}-${binIndex}`,
        name: bin.name,
        material: bin.material ?? '',
        materialId: bin.materialId ?? null,
        unitOfMeasure: bin.unitOfMeasure ?? null,
        owner: bin.owner ?? null,
        quantity: bin.quantity ?? 0,
        minQuantity: bin.minQuantity ?? null,
        maxQuantity: bin.maxQuantity ?? null,
        status: bin.status ?? (bin.materialId ? 'ok' : 'empty'),
      })),
    })),
  };
}

interface ApiSiteDetail {
  id: string;
  type: string;
  name: string;
  number: string | null;
  owner: string;
  latitude: number | null;
  longitude: number | null;
  description: string | null;
  serviceAreaId?: string;
  foremanAreaIds?: string[];
  locations: {
    id: string;
    name: string;
    type: string;
    lastCountAt?: string | null;
    bins: {
      id: string;
      code: string;
      owner: string | null;
      material: {
        id: string;
        code: string | null;
        description: string | null;
        unitOfMeasure: string | null;
        type: string | null;
      } | null;
      quantity: number;
      ledgerVersion: number;
      unitCost: number | null;
      minQuantity: number | null;
      maxQuantity: number | null;
    }[];
  }[];
}

function mapSiteDetail(row: ApiSiteDetail): SiteWithLocations {
  return {
    id: row.id,
    type: asSiteType(row.type),
    name: row.name,
    number: row.number,
    owner: asOwner(row.owner),
    latitude: row.latitude,
    longitude: row.longitude,
    description: row.description,
    serviceAreaId: row.serviceAreaId ?? '',
    foremanAreaIds: row.foremanAreaIds ?? [],
    locations: (row.locations ?? []).map((location) => ({
      id: location.id,
      name: location.name,
      kind: location.type,
      lastCountAt: location.lastCountAt ?? null,
      bins: (location.bins ?? []).map((bin) => ({
        id: bin.id,
        code: bin.code,
        owner: bin.owner,
        material: bin.material,
        quantity: bin.quantity ?? 0,
        ledgerVersion: bin.ledgerVersion ?? 0,
        unitCost: bin.unitCost ?? null,
        minQuantity: bin.minQuantity ?? null,
        maxQuantity: bin.maxQuantity ?? null,
      })),
    })),
  };
}

type ApiBinDetail = ApiSiteDetail['locations'][number]['bins'][number];

function mapBinDetail(bin: ApiBinDetail): StorageBin {
  return {
    id: bin.id,
    code: bin.code,
    owner: bin.owner,
    material: bin.material,
    quantity: bin.quantity ?? 0,
    ledgerVersion: bin.ledgerVersion ?? 0,
    unitCost: bin.unitCost ?? null,
    minQuantity: bin.minQuantity ?? null,
    maxQuantity: bin.maxQuantity ?? null,
  };
}

/** What the site form sends. Ids are strings on the client and numbers on the wire. */
export interface SiteFormInput {
  name: string;
  number: string | null;
  type: 'pit' | 'yard';
  owner: string | null;
  serviceAreaId: string;
  foremanAreaIds: string[];
  latitude: number | null;
  longitude: number | null;
  description: string | null;
}

export interface StorageLocationFormInput {
  name: string;
  /** StorageLocationType, e.g. STOCKPILE. */
  type: string;
}

/** A new bin, or the fields of one being edited. `unitCost` is only sent by a cost manager. */
export interface BinFormInput {
  code: string;
  materialId: string | null;
  owner: string | null;
  minQuantity: number | null;
  maxQuantity: number | null;
  unitCost?: number | null;
}

/**
 * One write to the locations API. Rejects with the server's own message (a 409 names the
 * clashing code or location), so the form can show it as it is.
 */
async function send<T>(method: 'POST' | 'PATCH' | 'DELETE', path: string, fallback: string, body?: unknown): Promise<T> {
  const response = await fetch(`${API_URL}/locations${path}`, {
    method,
    credentials: 'include',
    headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) {
    let message = fallback;
    try {
      const payload = await response.json();
      message = payload?.error || payload?.details || message;
    } catch {
      // keep default
    }
    throw new Error(message);
  }
  return (response.status === 204 ? undefined : await response.json()) as T;
}

const sitePayload = (input: Partial<SiteFormInput>) => ({
  ...input,
  ...(input.serviceAreaId !== undefined ? { serviceAreaId: Number(input.serviceAreaId) } : {}),
  ...(input.foremanAreaIds !== undefined ? { foremanAreaIds: input.foremanAreaIds.map(Number) } : {}),
});

const binPayload = (input: Partial<BinFormInput>) => ({
  ...input,
  ...(input.materialId !== undefined ? { materialId: input.materialId === null ? null : Number(input.materialId) } : {}),
});

const sitePath = (siteId: string) => `/sites/${encodeURIComponent(siteId)}`;
const locationPath = (siteId: string, locationId: string) => `${sitePath(siteId)}/locations/${encodeURIComponent(locationId)}`;

export const locationsService = {
  /**
   * One site with its locations and bins. Resolves to null when the API answers 404, which
   * covers both a missing id and a site outside the caller's service area.
   */
  getSite: async (id: string): Promise<SiteWithLocations | null> => {
    const response = await fetch(`${API_URL}/locations/sites/${encodeURIComponent(id)}`, {
      credentials: 'include',
    });
    if (response.status === 404) return null;
    if (!response.ok) {
      let message = 'Failed to load site';
      try {
        const body = await response.json();
        message = body?.error || body?.details || message;
      } catch {
        // keep default
      }
      throw new Error(message);
    }
    const body = (await response.json()) as { site: ApiSiteDetail };
    return mapSiteDetail(body.site);
  },

  // --- structure (materials:locations_manage) -------------------------------------------------

  /** A new pit or yard. Resolves to the site as the site window shows it. */
  createSite: async (input: SiteFormInput): Promise<SiteWithLocations> => {
    const body = await send<{ site: ApiSiteDetail }>('POST', '/sites', 'Failed to add the site', sitePayload(input));
    return mapSiteDetail(body.site);
  },

  /** Only the fields given change. Moving service area needs the foreman areas restated. */
  updateSite: async (siteId: string, patch: Partial<SiteFormInput>): Promise<SiteWithLocations> => {
    const body = await send<{ site: ApiSiteDetail }>('PATCH', sitePath(siteId), 'Failed to save the site', sitePayload(patch));
    return mapSiteDetail(body.site);
  },

  createStorageLocation: async (siteId: string, input: StorageLocationFormInput): Promise<void> => {
    await send('POST', `${sitePath(siteId)}/locations`, 'Failed to add the location', input);
  },

  updateStorageLocation: async (siteId: string, locationId: string, patch: Partial<StorageLocationFormInput>): Promise<void> => {
    await send('PATCH', locationPath(siteId, locationId), 'Failed to save the location', patch);
  },

  /** Refused (409) while the location still has bins. */
  deleteStorageLocation: async (siteId: string, locationId: string): Promise<void> => {
    await send('DELETE', locationPath(siteId, locationId), 'Failed to remove the location');
  },

  /**
   * Adds a bin to a location. Rejects with the server's message when the code is already used
   * in that location (409) or the location is not visible to the caller (404).
   */
  createBin: async (siteId: string, locationId: string, input: BinFormInput): Promise<StorageBin> => {
    const body = await send<{ bin: ApiBinDetail }>(
      'POST',
      `${locationPath(siteId, locationId)}/bins`,
      'Failed to add the bin',
      binPayload(input)
    );
    return mapBinDetail(body.bin);
  },

  /** Changing the material is refused (409) while the bin holds stock. */
  updateBin: async (siteId: string, locationId: string, binId: string, patch: Partial<BinFormInput>): Promise<StorageBin> => {
    const body = await send<{ bin: ApiBinDetail }>(
      'PATCH',
      `${locationPath(siteId, locationId)}/bins/${encodeURIComponent(binId)}`,
      'Failed to save the bin',
      binPayload(patch)
    );
    return mapBinDetail(body.bin);
  },

  /** Refused (409) for a bin that has ever moved stock or carries a certificate. */
  deleteBin: async (siteId: string, locationId: string, binId: string): Promise<void> => {
    await send('DELETE', `${locationPath(siteId, locationId)}/bins/${encodeURIComponent(binId)}`, 'Failed to remove the bin');
  },

  listSites: async (): Promise<Site[]> => {
    const response = await fetch(`${API_URL}/locations/sites`, {
      credentials: 'include',
    });
    if (!response.ok) {
      let message = 'Failed to load sites';
      try {
        const body = await response.json();
        message = body?.error || body?.details || message;
      } catch {
        // keep default
      }
      throw new Error(message);
    }
    const body = (await response.json()) as SitesResponse;
    return (body.sites ?? []).map(mapSite);
  },
};
